class LDRecommendedRitual extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.viewport = this.querySelector('[data-ld-pdp-rec-viewport]');
    this.track = this.querySelector('[data-ld-pdp-rec-track]');
    this.slides = Array.from(this.querySelectorAll('[data-ld-pdp-rec-slide]'));
    this.previews = this.querySelector('[data-ld-pdp-rec-previews]');
    this.currentOutput = this.querySelector('[data-ld-pdp-rec-current]');
    this.totalOutput = this.querySelector('[data-ld-pdp-rec-total]');
    this.prevButton = this.querySelector('[data-ld-pdp-rec-prev]');
    this.nextButton = this.querySelector('[data-ld-pdp-rec-next]');

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.dataset.initialized = 'true';
    this.activeIndex = 0;
    this.scrollFrame = 0;
    this.previewButtons = [];
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.buildPreviews();
    this.bindEvents();
    this.updateState(0);
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    this.viewport?.removeEventListener('keydown', this.handleKeydown);
    this.prevButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  buildPreviews() {
    if (!this.previews) return;

    this.previews.textContent = '';

    this.slides.forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ld-pdp-rec__preview';
      button.setAttribute('aria-label', `Mostra ${slide.dataset.previewTitle || `prodotto ${index + 1}`}`);
      button.addEventListener('click', () => this.goTo(index));

      const imageWrap = document.createElement('span');
      imageWrap.className = 'ld-pdp-rec__preview-image';

      if (slide.dataset.previewImage) {
        const image = document.createElement('img');
        image.src = slide.dataset.previewImage;
        image.alt = '';
        image.loading = 'lazy';
        imageWrap.append(image);
      }

      const title = document.createElement('span');
      title.className = 'ld-pdp-rec__preview-title';
      title.textContent = slide.dataset.previewTitle || `Prodotto ${index + 1}`;

      button.append(imageWrap, title);
      this.previews.append(button);
      this.previewButtons.push(button);
    });
  }

  bindEvents() {
    this.handleScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateFromScroll();
      });
    };

    this.handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      }
    };

    this.handlePrevious = () => this.goTo(this.activeIndex - 1);
    this.handleNext = () => this.goTo(this.activeIndex + 1);

    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.handleKeydown);
    this.prevButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.goTo(this.activeIndex, false));
      this.resizeObserver.observe(this.viewport);
    }
  }

  updateFromScroll() {
    const viewportLeft = this.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - viewportLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== this.activeIndex) this.updateState(closestIndex);
  }

  goTo(index, animate = true) {
    const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const targetSlide = this.slides[targetIndex];
    if (!targetSlide) return;

    this.viewport.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: animate && !this.prefersReducedMotion.matches ? 'smooth' : 'auto',
    });

    this.updateState(targetIndex);
  }

  updateState(index) {
    this.activeIndex = index;

    if (this.currentOutput) this.currentOutput.textContent = String(index + 1).padStart(2, '0');
    if (this.totalOutput) this.totalOutput.textContent = String(this.slides.length).padStart(2, '0');

    this.previewButtons.forEach((button, buttonIndex) => {
      const active = buttonIndex === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const activePreview = this.previewButtons[index];
    if (activePreview && this.previews) {
      const targetLeft = activePreview.offsetLeft - Math.max(0, (this.previews.clientWidth - activePreview.clientWidth) / 2);
      this.previews.scrollTo({
        left: targetLeft,
        behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('ld-recommended-ritual')) {
  customElements.define('ld-recommended-ritual', LDRecommendedRitual);
}
